import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Clock, Headphones, Building2, BadgePercent } from 'lucide-react';
import { useRef } from 'react';

const HomePage = () => {
  const carouselRef = useRef(null);

  const benefits = [
    { icon: Clock, title: 'Cotización en 24hrs', desc: 'Propuesta detallada garantizada' },
    { icon: Headphones, title: 'Soporte Integral', desc: 'Acompañamiento en todo momento' },
    { icon: Building2, title: 'Alianzas Corporativas', desc: 'Acceso a empresas líderes' },
    { icon: BadgePercent, title: 'Tarifas Institucionales', desc: 'Precios preferenciales para grupos' },
  ];

  // Empresas e instituciones que confían en nosotros
  const trustedCompanies = [
    { name: 'BIMBO', location: 'Planta México' },
    { name: 'BMW', location: 'San Luis Potosí' },
    { name: 'BOLSA MEXICANA DE VALORES', location: 'CDMX' },
    { name: 'BBVA', location: 'Torre BBVA' },
    { name: 'ACTINVER', location: 'Corporativo' },
    { name: 'PWC', location: 'México' },
    { name: 'EY', location: 'Ernst & Young' },
    { name: 'BANORTE', location: 'Corporativo' },
    { name: 'INBURSA', location: 'Grupo Financiero' },
    { name: 'FEMSA', location: 'Monterrey' },
    { name: 'TELEVISA', location: 'San Ángel' },
    { name: 'JOSE CUERVO', location: 'Tequila, Jalisco' },
  ];

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 300;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <main data-testid="home-page" className="bg-[#0a0a0a]">
      {/* Hero Section - Single Image */}
      <section className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
        {/* Background Image - Graduation */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=1920&q=80"
            alt="Alumnos Graduados"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/90 via-[#1A1A1A]/60 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
            <div className="max-w-2xl">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-[#009DE0] rounded-full animate-pulse" />
                <span className="text-white/80 text-sm font-medium">Turismo Académico Premium</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[0.9] text-white mb-6">
                De México
                <br />
                <span className="text-[#009DE0]">para el Mundo</span>
              </h1>

              {/* Subtitle */}
              <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-10 max-w-xl">
                Somos una agencia de viajes de turismo académico a nivel nacional e internacional. 
                Diseñamos soluciones y experiencias académicas de primer nivel, llevando alumnos a 
                congresos, museos, instituciones y eventos relevantes de su carrera.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/destinos"
                  data-testid="hero-cta-primary"
                  className="flex items-center gap-2 bg-[#009DE0] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#007BB0] transition-all duration-300"
                >
                  Ver Destinos
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/nosotros"
                  className="flex items-center gap-2 text-white border border-white/30 px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
                >
                  Conocer Más
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group p-8 rounded-2xl bg-[#1A1A1A] border border-white/5 hover:border-[#009DE0]/30 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#009DE0]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#009DE0]/20 transition-colors">
                  <benefit.icon size={26} className="text-[#009DE0]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-white/50 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Companies Section - Premium Style */}
      <section className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-[#009DE0] font-semibold text-sm uppercase tracking-wider">Alianzas Estratégicas</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-3">
              Empresas e Instituciones que confían en nosotros
            </h2>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Navigation Buttons */}
            <button
              onClick={() => scrollCarousel('left')}
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center text-[#1A1A1A] shadow-lg hover:border-[#009DE0] hover:text-[#009DE0] transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => scrollCarousel('right')}
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center text-[#1A1A1A] shadow-lg hover:border-[#009DE0] hover:text-[#009DE0] transition-colors"
            >
              <ChevronRight size={24} />
            </button>

            {/* Carousel */}
            <div
              ref={carouselRef}
              className="flex gap-8 overflow-x-auto pb-4 scrollbar-hide scroll-smooth px-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {trustedCompanies.map((company, index) => (
                <div
                  key={index}
                  data-testid={`company-card-${index}`}
                  className="flex-shrink-0 w-64 text-center group"
                >
                  {/* Logo Container - Clean Premium Style */}
                  <div className="bg-white rounded-2xl p-8 mb-4 shadow-sm border border-gray-100 group-hover:border-[#009DE0]/30 group-hover:shadow-md transition-all duration-300">
                    <div className="h-16 flex items-center justify-center">
                      <span className="text-3xl font-black text-[#1A1A1A]/70 tracking-tight italic">
                        {company.name}
                      </span>
                    </div>
                  </div>
                  <p className="text-[#1A1A1A]/50 text-sm">{company.location}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
            <div>
              <span className="text-[#009DE0] font-semibold text-sm uppercase tracking-wider">Nuestros Servicios</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">
                Experiencias que transforman carreras
              </h2>
            </div>
            <Link
              to="/servicios"
              className="mt-6 lg:mt-0 inline-flex items-center gap-2 text-white/60 hover:text-[#009DE0] transition-colors font-medium"
            >
              Ver todos los servicios
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Congresos', desc: 'Acceso a eventos académicos internacionales', img: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=400&fit=crop' },
              { title: 'Visitas Empresariales', desc: 'Conoce empresas de talla mundial', img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop' },
              { title: 'Campus Tours', desc: 'Universidades de prestigio global', img: 'https://images.unsplash.com/photo-1562774053-701939374585?w=600&h=400&fit=crop' },
            ].map((service, index) => (
              <Link
                key={index}
                to="/servicios"
                className="group relative h-72 rounded-2xl overflow-hidden"
              >
                <img
                  src={service.img}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                  <p className="text-white/60 text-sm">{service.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#009DE0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            ¿Listo para tu próxima
            <br />
            aventura académica?
          </h2>
          <p className="text-lg text-white/80 mb-10">
            Cotización en menos de 24 horas. Viaje gratis para docentes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="bg-white text-[#009DE0] px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all"
            >
              Solicitar Cotización
            </Link>
            <a
              href="https://wa.me/522282728556"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#007BB0] text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-[#006699] transition-all flex items-center justify-center gap-2"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
